/*
Create Pay Calendar Step 1 - Define Calendar.

Allows a user to enter a unique pay calendar name and select the pay frequency. 
*/

import { forwardRef, RefObject } from 'react';

import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Portal,
  Stack,
} from '@chakra-ui/react';

import { Controller, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import {
  DropdownInput,
  CrossOutline,
  ArrowRightOutline,
  ArrowLeftOutline,
} from '@aslanpay/portal-common-lib';

import { useCreatePayCalendarStore } from 'app/store';

import { payCalendarDefineCalendarFormSchema } from 'app/utils/schemas';

import { getPayFrequencyOptions } from 'app/components/PayCalendars/options';

import { PayCalendarDefineCalendarFormFields, PayCalendarFormProps } from '../types';

const PayCalendarDefineCalendarForm = forwardRef<HTMLElement, PayCalendarFormProps>(
  ({ id, onStepAction, previousStep, canPrevStep }, ref) => {
    const payCalendarData = useCreatePayCalendarStore((state) => state.payCalendarData);
    const setPayCalendarData = useCreatePayCalendarStore((state) => state.setPayCalendarData);

    const {
      register,
      control,
      handleSubmit,
      watch,
      resetField,
      setFocus,
      getValues,
      formState: { errors, isValid },
    } = useForm<PayCalendarDefineCalendarFormFields>({
      mode: 'onChange',
      defaultValues: payCalendarData as PayCalendarDefineCalendarFormFields,
      resolver: yupResolver(payCalendarDefineCalendarFormSchema),
    });

    const watchName = watch('name');

    const handleNextStep = (data: PayCalendarDefineCalendarFormFields) => {
      setPayCalendarData(data);
      onStepAction();
    };

    const payFrequencyOptions = getPayFrequencyOptions();

    const resetAndFocusField = (field: keyof PayCalendarDefineCalendarFormFields) => {
      resetField(field, { defaultValue: '' });
      setFocus(field);
    };

    const handleBackCLick = () => {
      const formValues = getValues();
      setPayCalendarData(formValues);
      previousStep();
    };

    return (
      <form id={id} onSubmit={handleSubmit(handleNextStep)} noValidate>
        <Stack spacing="20px">
          <FormControl isInvalid={!!errors?.name?.message} isRequired>
            <FormLabel>Pay calendar name </FormLabel>
            <InputGroup>
              <Input type="text" placeholder="E.g. “Monthly”" {...register('name')} />
              {watchName && (
                <InputRightElement>
                  <IconButton
                    aria-label="Clear name"
                    icon={<CrossOutline color="brand.darkGray" boxSize="24px" />}
                    variant="link"
                    onClick={() => resetAndFocusField('name')}
                  />
                </InputRightElement>
              )}
            </InputGroup>
            <FormErrorMessage>{errors?.name?.message}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={!!errors?.externalPayCalendarId?.message}>
            <FormLabel>External pay calendar ID</FormLabel>
            <Input
              type="text"
              placeholder="Optional — matching pay calendar id in the payroll provider (e.g. Workday)"
              {...register('externalPayCalendarId')}
            />
            <FormErrorMessage>{errors?.externalPayCalendarId?.message}</FormErrorMessage>
          </FormControl>
          <FormControl isInvalid={!!errors?.payFrequency?.message} isRequired>
            <FormLabel>Pay frequency</FormLabel>
            <Controller
              control={control}
              name="payFrequency"
              render={({ field: { onChange, onBlur, value } }) => (
                <DropdownInput
                  placeholder="What frequency do you pay your employees?"
                  defaultValue={payFrequencyOptions.find((frequency) => frequency.value === value)}
                  options={payFrequencyOptions}
                  onChange={onChange}
                  onBlur={onBlur}
                />
              )}
            />
            <FormErrorMessage>{errors?.payFrequency?.message}</FormErrorMessage>
          </FormControl>
          <Portal containerRef={ref as RefObject<HTMLElement>}>
            <Button
              size="sm"
              variant="outline"
              isDisabled={!canPrevStep}
              leftIcon={<ArrowLeftOutline boxSize="20px" />}
              onClick={handleBackCLick}
              mr="16px"
            >
              Back
            </Button>
            <Button
              form={id}
              type="submit"
              size="sm"
              isDisabled={!isValid}
              rightIcon={<ArrowRightOutline boxSize="20px" />}
            >
              Next
            </Button>
          </Portal>
        </Stack>
      </form>
    );
  }
);

PayCalendarDefineCalendarForm.displayName = 'PayCalendarDefinitionForm';

export default PayCalendarDefineCalendarForm;
